// "Match" questions (22 Oct 2026, Year 2): 3 to 5 items on the left (words or
// a picture, such as a clock) and as many partners on the right (words, such
// as "half past 3"), and every item is joined to exactly one partner, no
// partner used twice. An answer is sent, and its answer key is stored, as
// one string per item, "pair=<item id>-<partner id>" (for example "pair=a-r"),
// and the two are marked by comparing those strings (the set_match rule).
//
// Item ids and partner ids are single lower-case letters. They are separate
// lists, so a partner may have the same letter as an item. The "pair=" in
// front tells a match answer apart from every other answer by the text alone.

import {
  readPracticeDiagram,
  type PracticeDiagramSpec,
} from './practice-diagram-data';

export const MATCH_FEWEST = 3;
export const MATCH_MOST = 5;

const MATCH_ID = /^[a-z]$/;

export function isMatchId(id: unknown): id is string {
  return typeof id === 'string' && MATCH_ID.test(id);
}

// One item's entry, "pair=a-r". Null when either id is not a single letter.
export function matchEntry(itemId: string, partnerId: string): string | null {
  return isMatchId(itemId) && isMatchId(partnerId)
    ? `pair=${itemId}-${partnerId}`
    : null;
}

// The other way: "pair=a-r" back into its two ids. Null for anything else.
export function parseMatchEntry(
  entry: unknown,
): { itemId: string; partnerId: string } | null {
  if (typeof entry !== 'string') return null;
  const match = /^pair=([a-z])-([a-z])$/.exec(entry);
  if (!match) return null;
  const [, itemId = '', partnerId = ''] = match;
  return { itemId, partnerId };
}

// An item on the left: words, a picture, or both. A partner on the right is
// always words.
export type MatchItem = {
  id: string;
  label: string;
  diagram: PracticeDiagramSpec | null;
};
export type MatchPartner = { id: string; label: string };
export type MatchQuestion = { left: MatchItem[]; right: MatchPartner[] };

function hasUniqueIds(list: { id: string }[]): boolean {
  return new Set(list.map((entry) => entry.id)).size === list.length;
}

function inRange(list: unknown): list is unknown[] {
  return (
    Array.isArray(list) &&
    list.length >= MATCH_FEWEST &&
    list.length <= MATCH_MOST
  );
}

// The question's own items and partners, read defensively from what is sent
// to the screen: 3 to 5 of each, the same number of each, single-letter ids,
// no id twice in one list. Null for anything else, so the screen falls back
// to the plain answer box, like any form it does not know.
export function readMatch(content: unknown): MatchQuestion | null {
  if (typeof content !== 'object' || content === null) return null;
  const record = content as Record<string, unknown>;
  if (!inRange(record.left) || !inRange(record.right)) return null;
  if (record.left.length !== record.right.length) return null;
  const left: MatchItem[] = [];
  for (const entry of record.left) {
    if (typeof entry !== 'object' || entry === null) return null;
    const item = entry as Record<string, unknown>;
    if (!isMatchId(item.id)) return null;
    const label = typeof item.label === 'string' ? item.label.trim() : '';
    if (item.label !== undefined && label === '') return null;
    const diagram =
      item.diagram === undefined
        ? null
        : readPracticeDiagram({ diagram: item.diagram });
    if (item.diagram !== undefined && diagram === null) return null;
    if (label === '' && diagram === null) return null;
    left.push({ id: item.id, label, diagram });
  }
  const right: MatchPartner[] = [];
  for (const entry of record.right) {
    if (typeof entry !== 'object' || entry === null) return null;
    const partner = entry as Record<string, unknown>;
    if (!isMatchId(partner.id)) return null;
    if (typeof partner.label !== 'string' || partner.label.trim() === '')
      return null;
    right.push({ id: partner.id, label: partner.label.trim() });
  }
  return hasUniqueIds(left) && hasUniqueIds(right) ? { left, right } : null;
}

// The practice screen keeps every answer as one piece of text. For matching
// that is one line per joined item, "item=partner"; an item not joined yet
// has no line. Only this question's own items and partners are read back,
// and a partner is never read as joined to two items.
export function splitMatchChoices(
  question: MatchQuestion,
  value: string,
): Record<string, string> {
  const choices: Record<string, string> = {};
  const used = new Set<string>();
  for (const line of value.split('\n')) {
    const cut = line.indexOf('=');
    if (cut <= 0) continue;
    const itemId = line.slice(0, cut);
    const partnerId = line.slice(cut + 1);
    if (
      question.left.some((item) => item.id === itemId) &&
      question.right.some((partner) => partner.id === partnerId) &&
      !used.has(partnerId) &&
      choices[itemId] === undefined
    ) {
      choices[itemId] = partnerId;
      used.add(partnerId);
    }
  }
  return choices;
}

// Empty text while nothing is joined, so "nothing answered yet" reads the
// same as it does for every other kind of question.
export function joinMatchChoices(
  question: MatchQuestion,
  choices: Record<string, string>,
): string {
  return question.left
    .flatMap((item) => {
      const partnerId = choices[item.id];
      return partnerId === undefined ? [] : [`${item.id}=${partnerId}`];
    })
    .join('\n');
}

// A tap on a partner under an item. Tapping the partner already joined to
// it takes the join away; tapping another joins that one instead. A partner
// joined to a different item moves to this one, so no partner is ever used
// twice.
export function toggleMatch(
  question: MatchQuestion,
  value: string,
  itemId: string,
  partnerId: string,
): string {
  const choices = splitMatchChoices(question, value);
  const wasJoined = choices[itemId] === partnerId;
  const kept = Object.fromEntries(
    Object.entries(choices).filter(
      ([item, partner]) => item !== itemId && partner !== partnerId,
    ),
  );
  return joinMatchChoices(
    question,
    wasJoined ? kept : { ...kept, [itemId]: partnerId },
  );
}

// How many items are joined so far.
export function joinedCount(question: MatchQuestion, value: string): number {
  return Object.keys(splitMatchChoices(question, value)).length;
}

// What is sent: one "pair=<item>-<partner>" for each item, in the items' own
// order. Null until every item is joined.
export function matchOrder(
  question: MatchQuestion,
  value: string,
): string[] | null {
  const choices = splitMatchChoices(question, value);
  const order: string[] = [];
  for (const item of question.left) {
    const entry = matchEntry(item.id, choices[item.id] ?? '');
    if (entry === null) return null;
    order.push(entry);
  }
  return order;
}

// The answer key from a question's answer as the question bank writes it
// ({"a": "r", "b": "p", ...}), for whatever loads the questions. Written by
// matchEntry, the same function that writes a child's answer. Null unless
// every item has exactly one partner of the question and no partner is used
// twice.
export function matchKey(
  answer: unknown,
  itemIds: string[],
  partnerIds: string[],
): string[] | null {
  if (typeof answer !== 'object' || answer === null || Array.isArray(answer))
    return null;
  const record = answer as Record<string, unknown>;
  if (Object.keys(record).length !== itemIds.length) return null;
  const key: string[] = [];
  const used = new Set<string>();
  for (const itemId of itemIds) {
    const partnerId = record[itemId];
    if (
      typeof partnerId !== 'string' ||
      !partnerIds.includes(partnerId) ||
      used.has(partnerId)
    )
      return null;
    const entry = matchEntry(itemId, partnerId);
    if (entry === null) return null;
    used.add(partnerId);
    key.push(entry);
  }
  return key.sort();
}
